<script setup lang="ts">
import { computed } from 'vue';
import {
  faUser,
  faArrowRightFromBracket,
  faPhone,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useTenantStore, formatPhone } from '@sera/core';
import SidebarNavItem from './SidebarNavItem.vue';

const props = withDefaults(
  defineProps<{
    displayName: string;
    initials: string;
    profileActive: boolean;
    collapsed?: boolean;
    avatarSize?: 'sm' | 'md';
    itemHeight?: string;
  }>(),
  {
    collapsed: false,
    avatarSize: 'md',
    itemHeight: 'h-12',
  },
);

const emit = defineEmits<{
  logout: [];
  navigate: [];
}>();

// Read the tenant store directly (no new prop) to match the TokenBrandStrip /
// PublicShell convention for tenant-phone surfacing in libs/ui.
const tenantStore = useTenantStore();
const tenantPhone = computed(() => tenantStore.config?.tenantPhone ?? '');
// Raw digits power the tel: href; the visible text uses formatPhone().
const phoneDigits = computed(() => tenantPhone.value.replace(/\D/g, ''));
const phoneDisplay = computed(() => formatPhone(tenantPhone.value));
</script>

<template>
  <div class="shrink-0 border-t border-border pb-1">
    <!-- Avatar + name -->
    <div
      class="flex items-center overflow-hidden transition-[max-height,opacity,padding] duration-200"
      :class="[
        avatarSize === 'sm' ? 'gap-[10px] px-4' : 'gap-3 px-4',
        collapsed ? 'max-h-0 opacity-0 py-0' : 'max-h-[60px] opacity-100 py-3',
      ]"
    >
      <span
        class="rounded-full bg-primary text-white font-semibold flex items-center justify-center shrink-0"
        :class="
          avatarSize === 'sm' ? 'size-8 text-xs' : 'size-9 text-sm inline-flex'
        "
        >{{ initials }}</span
      >
      <span class="text-sm font-semibold text-foreground truncate">{{
        displayName
      }}</span>
    </div>

    <SidebarNavItem
      to="/profile"
      label="Profile"
      :icon="faUser"
      :active="profileActive"
      :collapsed="collapsed"
      :item-height="itemHeight"
      @click="emit('navigate')"
    />

    <SidebarNavItem
      label="Logout"
      :icon="faArrowRightFromBracket"
      :collapsed="collapsed"
      :item-height="itemHeight"
      @click="emit('logout')"
    />

    <!-- Contact us: tappable tel: link below Logout. Hidden when the tenant has
         no phone. Mirrors the SidebarNavItem icon-column / label collapse
         rhythm: fixed 53px centered icon column, then a two-line label/number
         wrapper that animates out when the sidebar collapses. -->
    <a
      v-if="tenantPhone"
      :href="`tel:${phoneDigits}`"
      data-testid="sidebar-contact-us"
      class="mt-2 flex items-center w-full whitespace-nowrap overflow-hidden border-l-[3px] border-transparent text-foreground transition-[background,color] duration-120 hover:bg-canvas py-2"
    >
      <span class="shrink-0 w-[53px] flex items-center justify-center">
        <FontAwesomeIcon
          :icon="faPhone"
          class="w-[18px] text-[0.9375rem] text-muted"
        />
      </span>
      <span
        data-testid="sidebar-contact-us-text"
        class="flex flex-col leading-tight overflow-hidden pr-4 transition-[max-width,opacity] duration-200"
        :class="collapsed ? 'max-w-0 opacity-0' : 'max-w-full opacity-100'"
      >
        <span class="text-sm font-medium">Contact us</span>
        <span class="text-xs text-muted">{{ phoneDisplay }}</span>
      </span>
    </a>
  </div>
</template>
